import { useState } from 'react';
import type { ExerciseMediaRow } from '../../lib/types';
import { deleteMediaRow, insertMediaRow, updateMediaRow } from './api';
import { IMAGE_TARGETS } from '../../lib/image';
import { captureVideoPoster } from '../../lib/poster';
import { releaseStaged, stageImage, type StagedImage } from '../../lib/staging';
import { exerciseMediaPath } from '../../lib/storagePaths';
import { validateVideoFile, validateVideoSize } from '../../lib/validators';
import { deleteMedia, mediaUrl, uploadMedia } from '../../lib/storage';
import { friendlyErrorMessage } from '../../lib/errors';
import { reorder, sortAssignments } from '../../lib/reorder';
import { useToast } from '../../components/Toast';
import { Button } from '../../components/Button';
import { Dropzone } from '../../components/Dropzone';
import { TextField } from '../../components/Field';
import { PreviewModal } from '../preview/PreviewModal';
import { buildHeroItems, type ExerciseInfo, type ExerciseSubject, type PendingHeroMedia } from '../preview/subject';

type Props = {
  exerciseId: string;
  media: ExerciseMediaRow[];
  info: ExerciseInfo;
  onChanged: () => void;
};

export function exercisePreview(info: ExerciseInfo, media: ExerciseMediaRow[], focus?: { pending: PendingHeroMedia } | { rowId: string }): ExerciseSubject {
  return { kind: 'exercise', ...info, ...buildHeroItems(media, mediaUrl, focus) };
}

function imageMeta(staged: StagedImage) {
  return { mime: staged.mime, bytes: staged.blob.size, width: staged.width, height: staged.height };
}

const clickable = { cursor: 'zoom-in' } as const;

/** The one `demo_photo` (rendition 'phone') -- replaceable, never more than one (unique index in 0025). */
export function DemoPhotoEditor({ exerciseId, media, info, onChanged }: Props) {
  const toast = useToast();
  const [compressing, setCompressing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [staged, setStaged] = useState<StagedImage | null>(null);
  const [viewing, setViewing] = useState(false);
  const demo = media.find((m) => m.kind === 'demo_photo' && m.rendition === 'phone');

  async function handleFile(file: File) {
    setCompressing(true);
    try {
      setStaged(await stageImage(file, IMAGE_TARGETS.hero));
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setCompressing(false);
    }
  }

  function cancel() {
    releaseStaged(staged);
    setStaged(null);
  }

  async function confirm() {
    if (!staged) return;
    setUploading(true);
    try {
      const path = exerciseMediaPath(exerciseId, staged.ext);
      await uploadMedia(path, staged.blob, staged.mime);
      if (demo) {
        await updateMediaRow(demo.id, { path, ...imageMeta(staged) });
        await deleteMedia([demo.path]);
      } else {
        await insertMediaRow({ exercise_id: exerciseId, kind: 'demo_photo', rendition: 'phone', path, sort: 0, ...imageMeta(staged) });
      }
      cancel();
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    if (!demo) return;
    try {
      await deleteMediaRow(demo.id);
      await deleteMedia([demo.path]);
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  const url = mediaUrl(demo?.path);
  return (
    <div className="field">
      <label>Demo photo</label>
      {url ? (
        <img src={url} alt="" onClick={() => setViewing(true)} style={{ width: 240, aspectRatio: '16/9', objectFit: 'cover', borderRadius: 14, marginBottom: 8, ...clickable }} />
      ) : null}
      <Dropzone
        accept="image/jpeg,image/png,image/webp,image/heic"
        label={compressing ? 'Compressing…' : demo ? 'Replace photo' : 'Drop a photo, or click to choose'}
        onFile={handleFile}
        disabled={compressing || uploading}
      />
      {demo ? (
        <div className="row" style={{ marginTop: 8 }}>
          <Button tone="tonal" size="sm" onClick={() => setViewing(true)} style={{ flex: 'initial' }}>
            Preview
          </Button>
          <Button tone="neutral" size="sm" onClick={handleRemove} style={{ flex: 'initial' }}>
            Remove
          </Button>
        </div>
      ) : null}
      {staged ? (
        <PreviewModal
          subject={exercisePreview(info, media, { pending: { kind: 'demo_photo', url: staged.url } })}
          stats={{ originalBytes: staged.originalBytes, finalBytes: staged.blob.size }}
          onConfirm={confirm}
          confirming={uploading}
          onClose={cancel}
        />
      ) : null}
      {viewing && demo ? <PreviewModal subject={exercisePreview(info, media, { rowId: demo.id })} onClose={() => setViewing(false)} /> : null}
    </div>
  );
}

/** `image` kind: any number of extra photos. */
export function ExtraImagesEditor({ exerciseId, media, info, onChanged }: Props) {
  const toast = useToast();
  const [compressing, setCompressing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [staged, setStaged] = useState<StagedImage | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const images = media.filter((m) => m.kind === 'image' && m.rendition === 'phone').sort((a, b) => a.sort - b.sort);

  async function handleFile(file: File) {
    setCompressing(true);
    try {
      setStaged(await stageImage(file, IMAGE_TARGETS.hero));
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setCompressing(false);
    }
  }

  function cancel() {
    releaseStaged(staged);
    setStaged(null);
  }

  async function confirm() {
    if (!staged) return;
    setUploading(true);
    try {
      const path = exerciseMediaPath(exerciseId, staged.ext);
      await uploadMedia(path, staged.blob, staged.mime);
      await insertMediaRow({ exercise_id: exerciseId, kind: 'image', rendition: 'phone', path, sort: images.length, ...imageMeta(staged) });
      cancel();
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove(row: ExerciseMediaRow) {
    try {
      await deleteMediaRow(row.id);
      await deleteMedia([row.path]);
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  return (
    <div className="field">
      <label>Extra images</label>
      <div className="row" style={{ flexWrap: 'wrap', marginBottom: 8 }}>
        {images.map((img) => {
          const url = mediaUrl(img.path);
          return (
            <div key={img.id} className="row-card" style={{ flex: 'initial', display: 'flex', alignItems: 'center', gap: 10 }}>
              {url ? <img className="thumb" src={url} alt="" onClick={() => setViewingId(img.id)} style={clickable} /> : null}
              <Button tone="danger" size="sm" onClick={() => handleRemove(img)}>
                Remove
              </Button>
            </div>
          );
        })}
      </div>
      <Dropzone
        accept="image/jpeg,image/png,image/webp,image/heic"
        label={compressing ? 'Compressing…' : 'Add an image'}
        onFile={handleFile}
        disabled={compressing || uploading}
      />
      {staged ? (
        <PreviewModal
          subject={exercisePreview(info, media, { pending: { kind: 'image', url: staged.url } })}
          stats={{ originalBytes: staged.originalBytes, finalBytes: staged.blob.size }}
          onConfirm={confirm}
          confirming={uploading}
          onClose={cancel}
        />
      ) : null}
      {viewingId ? <PreviewModal subject={exercisePreview(info, media, { rowId: viewingId })} onClose={() => setViewingId(null)} /> : null}
    </div>
  );
}

type StagedVideo = { file: File; url: string; poster: StagedImage; durationS: number };

/** `video` kind: many per exercise, orderable (up/down), title en/he, poster frame + duration captured client-side, mp4/mov only. */
export function VideosEditor({ exerciseId, media, info, onChanged }: Props) {
  const toast = useToast();
  const [preparing, setPreparing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [staged, setStaged] = useState<StagedVideo | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const videos = media.filter((m) => m.kind === 'video' && m.rendition === 'phone').sort((a, b) => a.sort - b.sort);

  async function handleFile(file: File) {
    const error = validateVideoFile(file) ?? validateVideoSize(file.size);
    if (error) {
      toast.show(error, 'error');
      return;
    }
    setPreparing(true);
    try {
      const { durationS, poster } = await captureVideoPoster(file, 1);
      const stagedPoster = await stageImage(poster, IMAGE_TARGETS.hero);
      setStaged({ file, url: URL.createObjectURL(file), poster: stagedPoster, durationS });
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setPreparing(false);
    }
  }

  function cancel() {
    if (staged) {
      URL.revokeObjectURL(staged.url);
      releaseStaged(staged.poster);
    }
    setStaged(null);
  }

  async function confirm() {
    if (!staged) return;
    const { file, poster, durationS } = staged;
    setUploading(true);
    try {
      const videoPath = exerciseMediaPath(exerciseId, file.name.split('.').pop() || 'mp4');
      const posterPath = exerciseMediaPath(exerciseId, poster.ext);
      await uploadMedia(videoPath, file, file.type || 'video/mp4');
      await uploadMedia(posterPath, poster.blob, poster.mime);
      await insertMediaRow({
        exercise_id: exerciseId,
        kind: 'video',
        rendition: 'phone',
        path: videoPath,
        poster_path: posterPath,
        sort: videos.length,
        mime: file.type || 'video/mp4',
        duration_s: Number.isFinite(durationS) ? Math.min(durationS, 600) : null,
        bytes: file.size,
      });
      cancel();
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove(row: ExerciseMediaRow) {
    try {
      await deleteMediaRow(row.id);
      await deleteMedia([row.path, row.poster_path]);
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  async function handleMove(index: number, dir: -1 | 1) {
    const reordered = reorder(videos, index, index + dir);
    const assignments = sortAssignments(reordered);
    try {
      await Promise.all(assignments.map((a) => updateMediaRow(a.id, { sort: a.sort })));
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  async function handleTitleChange(row: ExerciseMediaRow, field: 'title_en' | 'title_he', value: string) {
    try {
      await updateMediaRow(row.id, { [field]: value || null });
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  return (
    <div className="field">
      <label>Videos</label>
      {videos.map((v, index) => {
        const url = mediaUrl(v.poster_path ?? v.path);
        return (
          <div key={v.id} className="row-card" style={{ marginBottom: 10 }}>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              {url ? <img className="thumb-wide" src={url} alt="" onClick={() => setViewingId(v.id)} style={clickable} /> : <div className="thumb-wide" />}
              <div style={{ flex: 2 }}>
                <TextField label="Title (English)" defaultValue={v.title_en ?? ''} onBlur={(e) => handleTitleChange(v, 'title_en', e.target.value)} />
                <TextField label="Title (Hebrew)" dirAuto defaultValue={v.title_he ?? ''} onBlur={(e) => handleTitleChange(v, 'title_he', e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 'initial' }}>
                <button type="button" className="sort-handle" disabled={index === 0} onClick={() => handleMove(index, -1)}>
                  ↑
                </button>
                <button type="button" className="sort-handle" disabled={index === videos.length - 1} onClick={() => handleMove(index, 1)}>
                  ↓
                </button>
                <Button tone="danger" size="sm" onClick={() => handleRemove(v)}>
                  Remove
                </Button>
              </div>
            </div>
          </div>
        );
      })}
      <Dropzone
        accept="video/mp4,video/quicktime"
        label={preparing ? 'Preparing…' : 'Add a video (.mp4 / .mov, max 10 MB)'}
        onFile={handleFile}
        disabled={preparing || uploading}
      />
      {staged ? (
        <PreviewModal
          subject={exercisePreview(info, media, { pending: { kind: 'video', url: staged.url, poster: staged.poster.url } })}
          stats={{ originalBytes: staged.file.size, finalBytes: staged.file.size }}
          onConfirm={confirm}
          confirming={uploading}
          onClose={cancel}
        />
      ) : null}
      {viewingId ? <PreviewModal subject={exercisePreview(info, media, { rowId: viewingId })} onClose={() => setViewingId(null)} /> : null}
    </div>
  );
}
